import { useRef, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Section, Container } from '../../ui';
import styles from './ConformiteImpact.module.css';

export function ConformiteImpact() {
  const { t } = useTranslation('solutions');
  const ref = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const hasAnimated = useRef(false);

  const devices = t('regulatory.impact.devices', { returnObjects: true }) as {
    name: string; co2: string; water: string; metals: string;
  }[];

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasAnimated.current) {
          hasAnimated.current = true;
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const deviceIcons: Record<string, React.ReactNode> = {
    Smartphone: (
      <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <rect width="14" height="20" x="5" y="2" rx="2" ry="2" />
        <path d="M12 18h.01" />
      </svg>
    ),
    Tablette: (
      <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <rect width="16" height="20" x="4" y="2" rx="2" ry="2" />
        <line x1="12" x2="12.01" y1="18" y2="18" />
      </svg>
    ),
    Tablet: (
      <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <rect width="16" height="20" x="4" y="2" rx="2" ry="2" />
        <line x1="12" x2="12.01" y1="18" y2="18" />
      </svg>
    ),
    Laptop: (
      <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M20 16V7a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v9m16 0H4m16 0 1.28 2.55a1 1 0 0 1-.9 1.45H3.62a1 1 0 0 1-.9-1.45L4 16" />
      </svg>
    ),
  };

  return (
    <Section variant="light">
      <Container>
        <div ref={ref} className={`${styles.wrapper} ${isVisible ? styles.visible : ''}`}>
          <h2 className={styles.title}>{t('regulatory.impact.sectionTitle')}</h2>
          <div className={styles.grid}>
            {devices.map((device, i) => (
              <div
                key={i}
                className={styles.card}
                style={{ '--card-index': i } as React.CSSProperties}
              >
                <div className={styles.icon}>
                  {deviceIcons[device.name] || deviceIcons['Smartphone']}
                </div>
                <div className={styles.name}>{device.name}</div>
                <div className={styles.metrics}>
                  <div className={styles.metric}>
                    <div className={`${styles.metricIcon} ${styles.co2}`}>
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <path d="M2 22 16 8" />
                        <path d="M3.47 12.53 5 11l1.53 1.53a3.5 3.5 0 0 1 0 4.94L5 19l-1.53-1.53a3.5 3.5 0 0 1 0-4.94Z" />
                        <path d="M7.47 8.53 9 7l1.53 1.53a3.5 3.5 0 0 1 0 4.94L9 15l-1.53-1.53a3.5 3.5 0 0 1 0-4.94Z" />
                        <path d="M11.47 4.53 13 3l1.53 1.53a3.5 3.5 0 0 1 0 4.94L13 11l-1.53-1.53a3.5 3.5 0 0 1 0-4.94Z" />
                        <line x1="20" x2="20" y1="2" y2="22" />
                      </svg>
                    </div>
                    <div>
                      <span className={styles.metricValue}>-{device.co2}</span>
                      <span className={styles.metricLabel}> {t('regulatory.impact.co2Label')}</span>
                    </div>
                  </div>
                  <div className={styles.metric}>
                    <div className={`${styles.metricIcon} ${styles.water}`}>
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <path d="M12 22a7 7 0 0 0 7-7c0-2-1-3.9-3-5.5s-3.5-4-4-6.5c-.5 2.5-2 4.9-4 6.5C6 11.1 5 13 5 15a7 7 0 0 0 7 7z" />
                      </svg>
                    </div>
                    <div>
                      <span className={styles.metricValue}>-{device.water}</span>
                      <span className={styles.metricLabel}> {t('regulatory.impact.waterLabel')}</span>
                    </div>
                  </div>
                  <div className={styles.metric}>
                    <div className={`${styles.metricIcon} ${styles.metals}`}>
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <path d="M6 3h12l4 6-10 13L2 9Z" />
                        <path d="M11 3 8 9l4 13 4-13-3-6" />
                        <path d="M2 9h20" />
                      </svg>
                    </div>
                    <div>
                      <span className={styles.metricValue}>{device.metals}</span>
                      <span className={styles.metricLabel}> {t('regulatory.impact.metalsLabel')}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <p className={styles.source}>{t('regulatory.impact.source')}</p>
        </div>
      </Container>
    </Section>
  );
}
